export const TP_PLAN_ID = "car_tp" as const;
export const TP_PRO_FULL_PLAN_ID = "car_tp_pro_full" as const;
export const TP_PRO_LITE_PLAN_ID = "car_tp_pro_lite" as const;

export const PA_OWNER_ADDON_ID = "pa_owner_car" as const;
export const PASSENGER_ADDON_ID = "pa_unnamed_passenger_car" as const;
export const PAID_DRIVER_ADDON_ID = "legal_liability_car" as const;
export const RSA_ADDON_ID = "rsa_car" as const;

export type TpPlanId =
  | typeof TP_PLAN_ID
  | typeof TP_PRO_FULL_PLAN_ID
  | typeof TP_PRO_LITE_PLAN_ID;

export type TpAddonId =
  | typeof PA_OWNER_ADDON_ID
  | typeof PASSENGER_ADDON_ID
  | typeof PAID_DRIVER_ADDON_ID
  | typeof RSA_ADDON_ID;

const SUPPORTED_PLAN_IDS = new Set<string>([
  TP_PLAN_ID,
  TP_PRO_FULL_PLAN_ID,
  TP_PRO_LITE_PLAN_ID
]);

const SUPPORTED_ADDON_IDS = new Set<string>([
  PA_OWNER_ADDON_ID,
  PASSENGER_ADDON_ID,
  PAID_DRIVER_ADDON_ID,
  RSA_ADDON_ID
]);

const TP_PRO_PLAN_IDS = new Set<string>([
  TP_PRO_FULL_PLAN_ID,
  TP_PRO_LITE_PLAN_ID
]);

const PLAN_ORDER: Record<TpPlanId, number> = {
  [TP_PLAN_ID]: 0,
  [TP_PRO_LITE_PLAN_ID]: 1,
  [TP_PRO_FULL_PLAN_ID]: 2
};

type Price = {
  net_premium?: number | string | null;
  old_net_premium?: number | string | null;
  net_strike_through_premium?: number | string | null;
};

export type AddonVariant = {
  variant_id?: string | null;
  display_name?: string;
  title?: string;
  description?: string;
  detailed_description?: string;
  price?: Price;
  sum_assured_value?: number | null;
  tenure?: number | null;
  badge?: {
    text?: string;
    type?: string;
  };
  is_selected?: boolean;
};

type RawAddon = AddonVariant & {
  id?: string;
  sub_heading?: string;
  variants?: AddonVariant[];
  addon_variant_responses?: AddonVariant[];
  min_variant_price?: number | string | null;
};

type RawCover = {
  id?: string;
  key?: string;
};

export type PlanTag = {
  image?: string;
  text?: string;
  type?: string;
};

export type TpPlanFeature = {
  text: string;
  image?: string;
};

type PlanCover = {
  id?: string;
  display_name?: string;
  review_plan_name?: string;
  premium?: number | string | null;
  display_premium?: string;
  is_selected?: boolean;
  secondary_tag?: PlanTag;
  tp_edit_description_items?: Array<{
    text?: string;
    image?: string;
  }>;
  addons?: RawAddon[];
};

type EligiblePlan = {
  id?: string;
  display_name?: string;
  price?: Price;
  pre_included_covers?: RawCover[];
  addons?: RawAddon[];
  is_selected?: boolean;
};

export type PlansPayload = {
  covers?: PlanCover[];
  eligible_plans?: EligiblePlan[];
  selected_plan?: {
    id?: string;
  };
};

export type ProposalAddonSelection = {
  id: TpAddonId;
  variant_id?: string;
  tenure: number;
  sum_assured_value?: number;
};

export type TpAddonRefreshPayload = {
  id: TpAddonId;
  variant_id?: string;
};

export type AddonSelectionMap = Partial<
  Record<TpAddonId, ProposalAddonSelection>
>;

export type TpAddonModel = {
  id: TpAddonId;
  displayName: string;
  description: string;
  badge?: {
    text?: string;
    type?: string;
  };
  variantId?: string;
  tenure: number;
  sumAssuredValue?: number;
  netPremium: number;
  minimumNetPremium: number;
  variants: AddonVariant[];
};

export type TpPlanModel = {
  id: TpPlanId;
  displayName: string;
  reviewPlanName: string;
  baseNetPremium: number;
  secondaryTag?: PlanTag;
  features: TpPlanFeature[];
  addons: TpAddonModel[];
  bundledAddonIds: Set<TpAddonId>;
  hasInvalidAddonData: boolean;
  isSelectedFromPlans: boolean;
};

export type TpEditModel = {
  plans: TpPlanModel[];
  selectedPlanId?: TpPlanId;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isTpPlanId = (value?: string | null): value is TpPlanId =>
  Boolean(value && SUPPORTED_PLAN_IDS.has(value));

export const isTpProPlan = (planId: TpPlanId): boolean =>
  TP_PRO_PLAN_IDS.has(planId);

export const isTpAddonId = (value?: string | null): value is TpAddonId =>
  Boolean(value && SUPPORTED_ADDON_IDS.has(value));

const getVariants = (addon: RawAddon): AddonVariant[] => {
  if (Array.isArray(addon.variants) && addon.variants.length > 0) {
    return addon.variants;
  }
  return Array.isArray(addon.addon_variant_responses)
    ? addon.addon_variant_responses
    : [];
};

const getAddonModel = (
  coverAddon: RawAddon | undefined,
  rawAddon: RawAddon
): TpAddonModel | null => {
  const id = coverAddon?.id || rawAddon.id;
  if (!isTpAddonId(id)) return null;

  const mergedAddon = { ...rawAddon, ...coverAddon };
  const suppliedVariants = getVariants(mergedAddon);
  const variants = suppliedVariants.filter((variant) => {
    if (
      !variant.variant_id ||
      toFiniteNumber(variant.price?.net_premium) === null
    ) {
      return false;
    }

    return (
      id !== PA_OWNER_ADDON_ID ||
      toFiniteNumber(variant.sum_assured_value) !== null
    );
  });
  const configuredNetPremium = toFiniteNumber(mergedAddon.price?.net_premium);
  if (
    (suppliedVariants.length > 0 &&
      variants.length !== suppliedVariants.length) ||
    (suppliedVariants.length === 0 && configuredNetPremium === null)
  ) {
    return null;
  }

  const variantPremiums = variants
    .map((variant) => toFiniteNumber(variant.price?.net_premium))
    .filter((premium): premium is number => premium !== null);
  const netPremium = configuredNetPremium ?? variantPremiums[0];
  if (netPremium === undefined) return null;

  const configuredMinimum = toFiniteNumber(mergedAddon.min_variant_price);
  const minimumNetPremium =
    configuredMinimum ??
    (variantPremiums.length > 0 ? Math.min(...variantPremiums) : netPremium);
  const displayName = (
    mergedAddon.display_name ||
    mergedAddon.title ||
    ""
  ).trim();
  if (!displayName) return null;

  return {
    id,
    displayName,
    description:
      mergedAddon.detailed_description || mergedAddon.description || "",
    badge: mergedAddon.badge,
    variantId: mergedAddon.variant_id || undefined,
    tenure: toFiniteNumber(mergedAddon.tenure) ?? 1,
    sumAssuredValue: toFiniteNumber(mergedAddon.sum_assured_value) ?? undefined,
    netPremium,
    minimumNetPremium,
    variants
  };
};

const getPlanFeatures = (cover: PlanCover): TpPlanFeature[] => {
  if (!Array.isArray(cover.tp_edit_description_items)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[tp_only_renewals] ${
          cover.id || "unknown plan"
        } is missing tp_edit_description_items`
      );
    }
    return [];
  }

  return cover.tp_edit_description_items.flatMap((item) => {
    const text = typeof item?.text === "string" ? item.text.trim() : "";
    return text ? [{ text, image: item.image }] : [];
  });
};

export const buildTpEditModel = (
  plansPayload?: PlansPayload,
  selectedPlanId?: string
): TpEditModel => {
  const covers = Array.isArray(plansPayload?.covers) ? plansPayload.covers : [];
  const eligiblePlans = Array.isArray(plansPayload?.eligible_plans)
    ? plansPayload.eligible_plans
    : [];

  const plans = covers
    .filter((cover): cover is PlanCover & { id: TpPlanId } =>
      isTpPlanId(cover.id)
    )
    .map((cover): TpPlanModel | null => {
      const rawPlan = eligiblePlans.find((plan) => plan.id === cover.id);
      if (!rawPlan) return null;

      const baseNetPremium =
        toFiniteNumber(cover.premium) ??
        toFiniteNumber(rawPlan.price?.net_premium);
      if (baseNetPremium === null) return null;

      const bundledAddonIds = new Set<TpAddonId>();
      rawPlan.pre_included_covers?.forEach((includedCover) => {
        const id = includedCover.id || includedCover.key;
        if (isTpAddonId(id)) bundledAddonIds.add(id);
      });

      const rawAddons = Array.isArray(rawPlan.addons) ? rawPlan.addons : [];
      const supportedRawAddons = rawAddons.filter((addon) =>
        isTpAddonId(addon.id)
      );
      const addonModels = supportedRawAddons.map((rawAddon) =>
        getAddonModel(
          cover.addons?.find((addon) => addon.id === rawAddon.id),
          rawAddon
        )
      );
      const addons = addonModels.filter(
        (addon): addon is TpAddonModel => addon !== null
      );

      return {
        id: cover.id,
        displayName: cover.display_name?.trim() || "",
        reviewPlanName:
          cover.review_plan_name ||
          cover.display_name ||
          rawPlan.display_name ||
          cover.id,
        baseNetPremium,
        secondaryTag: cover.secondary_tag,
        features: getPlanFeatures(cover),
        addons,
        bundledAddonIds,
        hasInvalidAddonData: addonModels.some((addon) => addon === null),
        isSelectedFromPlans:
          cover.is_selected === true || rawPlan.is_selected === true
      };
    })
    .filter((plan): plan is TpPlanModel => plan !== null)
    .sort((left, right) => PLAN_ORDER[left.id] - PLAN_ORDER[right.id]);

  const selectedFromProps = isTpPlanId(selectedPlanId)
    ? selectedPlanId
    : undefined;
  const selectedFromPayload = isTpPlanId(plansPayload?.selected_plan?.id)
    ? plansPayload?.selected_plan?.id
    : undefined;
  const selectedFromPlan = plans.find((plan) => plan.isSelectedFromPlans)?.id;

  return {
    plans,
    selectedPlanId:
      selectedFromProps ||
      selectedFromPayload ||
      selectedFromPlan ||
      plans[0]?.id
  };
};

const getSelectedVariant = (
  addon: TpAddonModel,
  variantId?: string
): AddonVariant | undefined =>
  addon.variants.find((variant) => variant.variant_id === variantId) ||
  addon.variants.find((variant) => variant.variant_id === addon.variantId) ||
  addon.variants[0];

export const selectionFromAddon = (
  addon: TpAddonModel,
  source?: Partial<ProposalAddonSelection>
): ProposalAddonSelection => {
  const selectedVariant = getSelectedVariant(addon, source?.variant_id);
  const variantId =
    selectedVariant?.variant_id || source?.variant_id || addon.variantId;
  const sumAssuredValue =
    toFiniteNumber(selectedVariant?.sum_assured_value) ??
    toFiniteNumber(source?.sum_assured_value) ??
    addon.sumAssuredValue;

  return {
    id: addon.id,
    ...(variantId ? { variant_id: variantId } : {}),
    tenure:
      toFiniteNumber(selectedVariant?.tenure) ??
      toFiniteNumber(source?.tenure) ??
      addon.tenure,
    ...(sumAssuredValue !== undefined
      ? { sum_assured_value: sumAssuredValue }
      : {})
  };
};

const preserveSelection = (
  selection: ProposalAddonSelection
): ProposalAddonSelection => ({
  id: selection.id,
  ...(selection.variant_id ? { variant_id: selection.variant_id } : {}),
  tenure: toFiniteNumber(selection.tenure) ?? 1,
  ...(toFiniteNumber(selection.sum_assured_value) !== null
    ? { sum_assured_value: Number(selection.sum_assured_value) }
    : {})
});

export const isSelectionCompatible = (
  plan: TpPlanModel,
  addonId: TpAddonId
): boolean =>
  plan.addons.some((addon) => addon.id === addonId) ||
  (addonId === RSA_ADDON_ID && isTpProPlan(plan.id));

export const buildInitialSelections = (
  plans: TpPlanModel[],
  selectedPlanId: TpPlanId | undefined,
  proposalSelections: ProposalAddonSelection[] = []
): Partial<Record<TpPlanId, AddonSelectionMap>> => {
  const result: Partial<Record<TpPlanId, AddonSelectionMap>> = {};

  plans.forEach((plan) => {
    result[plan.id] = {};
  });

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  if (!selectedPlan) return result;

  const selectedPlanSelections: AddonSelectionMap = {};
  proposalSelections.forEach((selection) => {
    if (!isTpAddonId(selection?.id)) return;
    const addon = selectedPlan.addons.find((item) => item.id === selection.id);
    if (addon) {
      selectedPlanSelections[selection.id] = selectionFromAddon(
        addon,
        selection
      );
    } else if (selection.id === RSA_ADDON_ID && isTpProPlan(selectedPlan.id)) {
      selectedPlanSelections[selection.id] = preserveSelection(selection);
    }
  });
  result[selectedPlan.id] = selectedPlanSelections;

  return result;
};

export const carrySelectionsToPlan = (
  targetPlan: TpPlanModel,
  sourceSelections: AddonSelectionMap,
  targetSelections: AddonSelectionMap = {}
): AddonSelectionMap => {
  const carriedSelections: AddonSelectionMap = { ...targetSelections };

  Object.values(sourceSelections).forEach((selection) => {
    if (!selection || !isSelectionCompatible(targetPlan, selection.id)) return;
    const addon = targetPlan.addons.find((item) => item.id === selection.id);
    carriedSelections[selection.id] = addon
      ? selectionFromAddon(addon, selection)
      : preserveSelection(selection);
  });

  Object.keys(carriedSelections).forEach((addonId) => {
    if (isTpAddonId(addonId) && !isSelectionCompatible(targetPlan, addonId)) {
      delete carriedSelections[addonId];
    }
  });

  return carriedSelections;
};

export const getAddonSelectionPremium = (
  addon: TpAddonModel,
  selection?: ProposalAddonSelection
): number => {
  if (!selection) return 0;
  const variant = getSelectedVariant(addon, selection.variant_id);
  return toFiniteNumber(variant?.price?.net_premium) ?? addon.netPremium;
};

export const getDisplayAddonPremium = (
  addon: TpAddonModel,
  selection?: ProposalAddonSelection
): number =>
  selection
    ? getAddonSelectionPremium(addon, selection)
    : addon.minimumNetPremium;

export const calculateTotalNetPremium = (
  plan: TpPlanModel,
  selections: AddonSelectionMap,
  allPlans: TpPlanModel[]
): number => {
  const tpPlan = allPlans.find((item) => item.id === TP_PLAN_ID);

  return Object.values(selections).reduce((total, selection) => {
    if (!selection) return total;
    if (plan.bundledAddonIds.has(selection.id)) return total;

    const addon = plan.addons.find((item) => item.id === selection.id);
    if (addon) return total + getAddonSelectionPremium(addon, selection);

    // TP Pro Lite can retain an existing RSA selection even when its plan
    // response does not expose RSA as a removable add-on.
    if (selection.id === RSA_ADDON_ID) {
      const tpRsaAddon = tpPlan?.addons.find(
        (item) => item.id === RSA_ADDON_ID
      );
      return tpRsaAddon
        ? total + getAddonSelectionPremium(tpRsaAddon, selection)
        : total;
    }

    return total;
  }, plan.baseNetPremium);
};

export const getSelectableAddons = (plan: TpPlanModel): TpAddonModel[] =>
  plan.addons.filter(
    (addon) => addon.id !== RSA_ADDON_ID || !isTpProPlan(plan.id)
  );

export const areSelectionsValid = (
  plan: TpPlanModel,
  selections: AddonSelectionMap,
  allPlans: TpPlanModel[]
): boolean => {
  if (plan.hasInvalidAddonData) return false;

  const tpPlan = allPlans.find((item) => item.id === TP_PLAN_ID);

  return Object.values(selections).every((selection) => {
    if (!selection) return true;
    if (plan.bundledAddonIds.has(selection.id)) return true;

    const addon = plan.addons.find((item) => item.id === selection.id);
    const pricedAddon =
      addon ||
      (selection.id === RSA_ADDON_ID && isTpProPlan(plan.id)
        ? tpPlan?.addons.find((item) => item.id === RSA_ADDON_ID)
        : undefined);
    if (!pricedAddon) return false;

    return (
      pricedAddon.variants.length === 0 ||
      pricedAddon.variants.some(
        (variant) => variant.variant_id === selection.variant_id
      )
    );
  });
};

export const serializeSelections = (
  selections: AddonSelectionMap
): TpAddonRefreshPayload[] =>
  Object.values(selections)
    .filter((selection): selection is ProposalAddonSelection =>
      Boolean(selection && isTpAddonId(selection.id))
    )
    .map((selection) => ({
      id: selection.id,
      ...(selection.variant_id ? { variant_id: selection.variant_id } : {})
    }));
